import toSentenceCase from '../../../services/toSentenceCase';
import React, { ChangeEvent } from 'react';
import Row from '../../Common/Row';
import Dropdown from '../../Inputs/Dropdown';
import Inputs from '../../Inputs';
import Typography from 'kingpin/atoms/Typography';
import ChevronDownGrey from './ChevronDownGrey.svg';
import ChevronDown from './ChevronDown.png';
import ChevronUp from './ChevronUp.png';
import styled from 'styled-components';
import TextInput from '../../../kingpin/atoms/TextInput';
import { FISCAL_PERIODS_DISABLED_TOOLTIP } from '../../../constants';

const ModeChangeWrapper = styled.div`
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;

export const getLabel = (
  current: RelativeDateRange,
  interval: ExtendedInterval,
): string => {
  if (current.type === 'current') {
    return toSentenceCase(interval);
  }

  if (current.n && current.n > 1) {
    return `${toSentenceCase(interval)}s`;
  }

  return toSentenceCase(interval);
};

const RelativeDateBuilder = ({
  testPrefix,
  current,
  setCurrent,
  isAdvanced,
  onModeTogglePressed,
  isPlaceholder,
  isCalendarIntervalDisabled,
  isFiscalIntervalDisabled,
  isFiscalIntervalHidden,
}: {
  testPrefix: string;
  current: RelativeDateRange;
  setCurrent: (newD: RelativeDateRange) => void;
  isAdvanced?: boolean;
  onModeTogglePressed?: () => void;
  isPlaceholder?: boolean;
  isCalendarIntervalDisabled?: boolean;
  isFiscalIntervalDisabled?: boolean;
  isFiscalIntervalHidden?: boolean;
}) => (
  <div style={{ padding: 8 }}>
    <Row>
      {/* Inputs - Left */}
      <div>
        {/* Top */}
        <Row>
          <div style={{ width: 132, marginRight: 8 }}>
            <Dropdown
              isShort
              unsetWidth
              isPlaceholder={isPlaceholder}
              testId={`${testPrefix}relative-type-picker`}
              selectedLabel={toSentenceCase(current.type)}
              options={[
                {
                  label: 'Last',
                  onSelected: () => {
                    setCurrent({
                      ...current,
                      type: 'last',
                      n: current.n ? current.n : 1,
                      currentToDate: false,
                    });
                  },
                },
                {
                  label: 'Current',
                  onSelected: () => {
                    setCurrent({
                      ...current,
                      type: 'current',
                      n: undefined,
                      currentToDate: current.interval !== 'day',
                    });
                  },
                },
                {
                  label: 'Next',
                  onSelected: () => {
                    setCurrent({
                      ...current,
                      type: 'next',
                      n: current.n ? current.n : 1,
                      currentToDate: false,
                    });
                  },
                },
              ]}
            />
          </div>
          {current.type !== 'current' && (
            <div style={{ width: 132, marginRight: 8 }}>
              <TextInput
                inputSize="Small"
                data-testid={`${testPrefix}n`}
                type="number"
                style={
                  isPlaceholder ? { color: 'var(--color-grey-5)' } : undefined
                }
                state={current.n === undefined ? 'Error' : undefined}
                value={current.n ? current.n.toString(10) : ''}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  const newN = event.target.value;
                  if (newN === '') {
                    setCurrent({ ...current, n: undefined });
                  } else if (Number.parseInt(newN) > 0) {
                    setCurrent({ ...current, n: Number.parseInt(newN) });
                  }
                }}
                width={'100%'}
              />
            </div>
          )}

          <div style={{ width: 132 }}>
            <Dropdown
              isShort
              isPlaceholder={isPlaceholder}
              unsetWidth
              testId={`${testPrefix}relative-interval-picker`}
              selectedLabel={getLabel(current, current.interval)}
              options={[
                {
                  label: getLabel(current, 'day'),
                  isDisabled: isCalendarIntervalDisabled,
                  onSelected: () => {
                    setCurrent({
                      ...current,
                      interval: 'day',
                      currentToDate: false,
                    });
                  },
                },
                {
                  label: getLabel(current, 'week'),
                  onSelected: () => {
                    setCurrent({
                      ...current,
                      interval: 'week',
                      currentToDate: false,
                    });
                  },
                },
                {
                  label: getLabel(current, 'month'),
                  isDisabled: isCalendarIntervalDisabled,
                  onSelected: () => {
                    setCurrent({
                      ...current,
                      interval: 'month',
                      currentToDate: false,
                    });
                  },
                },
                {
                  label: getLabel(current, 'quarter'),
                  isDisabled: isCalendarIntervalDisabled,
                  onSelected: () => {
                    setCurrent({
                      ...current,
                      interval: 'quarter',
                      currentToDate: false,
                    });
                  },
                },
                {
                  label: getLabel(current, 'year'),
                  isDisabled: isCalendarIntervalDisabled,
                  onSelected: () => {
                    setCurrent({
                      ...current,
                      interval: 'year',
                      currentToDate: false,
                    });
                  },
                },
                {
                  label: getLabel(current, 'fiscalPeriod'),
                  isDisabled:
                    isFiscalIntervalDisabled || isFiscalIntervalHidden,
                  tooltip: isFiscalIntervalHidden
                    ? FISCAL_PERIODS_DISABLED_TOOLTIP
                    : undefined,
                  onSelected: () => {
                    setCurrent({
                      ...current,
                      interval: 'fiscalPeriod',
                      currentToDate: false,
                    });
                  },
                },
                {
                  label: getLabel(current, 'fiscalQuarter'),
                  isDisabled:
                    isFiscalIntervalDisabled || isFiscalIntervalHidden,
                  tooltip: isFiscalIntervalHidden
                    ? FISCAL_PERIODS_DISABLED_TOOLTIP
                    : undefined,
                  onSelected: () => {
                    setCurrent({
                      ...current,
                      interval: 'fiscalQuarter',
                      currentToDate: false,
                    });
                  },
                },
                {
                  label: getLabel(current, 'fiscalYear'),
                  isDisabled:
                    isFiscalIntervalDisabled || isFiscalIntervalHidden,
                  tooltip: isFiscalIntervalHidden
                    ? FISCAL_PERIODS_DISABLED_TOOLTIP
                    : undefined,
                  onSelected: () => {
                    setCurrent({
                      ...current,
                      interval: 'fiscalYear',
                      currentToDate: false,
                    });
                  },
                },
              ]}
            />
          </div>
        </Row>
        {/* Bottom */}
        {isAdvanced && onModeTogglePressed && (
          <ModeChangeWrapper>
            <Row
              style={{
                marginTop: 12,
                width: '100%',
                marginBottom: 4,
              }}
              centerAlign
              onClick={onModeTogglePressed}
            >
              <Typography.Body type="Link" color={'var(--color-info-v2)'}>
                Basic
              </Typography.Body>
              <img
                src={ChevronUp}
                style={{ marginLeft: 4, width: 8, height: 5 }}
                alt="up"
              />
            </Row>
          </ModeChangeWrapper>
        )}
        {!isAdvanced && (
          <>
            <Row
              style={{
                marginTop: 12,
                width: '100%',
                marginBottom: 4,
              }}
              spaceBetween
              centerAlign
            >
              {/* Switch to advanced */}
              <ModeChangeWrapper>
                {onModeTogglePressed && (
                  <Row centerAlign onClick={onModeTogglePressed}>
                    <Typography.Body
                      type={'Body 12'}
                      color={
                        isPlaceholder
                          ? 'var(--color-grey-5)'
                          : 'var(--color-info-v2)'
                      }
                    >
                      Advanced
                    </Typography.Body>
                    <img
                      src={isPlaceholder ? ChevronDownGrey : ChevronDown}
                      style={{ marginLeft: 4, width: 8, height: 5 }}
                      alt="down"
                    />
                  </Row>
                )}
              </ModeChangeWrapper>
              {/* To date */}
              <Row centerAlign>
                {!(
                  current.interval === 'day' && current.type === 'current'
                ) && (
                  <>
                    <div style={{ marginRight: 4 }}>
                      {current.type === 'current' && (
                        <Typography.Body
                          type={'Body 12'}
                          color={
                            isPlaceholder ? 'var(--color-grey-5)' : undefined
                          }
                        >
                          To Date
                        </Typography.Body>
                      )}
                      {current.type !== 'current' && (
                        <Typography.Body
                          type={'Body 12'}
                          color={
                            isPlaceholder ? 'var(--color-grey-5)' : undefined
                          }
                        >
                          {`Include ${
                            current.interval === 'day'
                              ? 'Today'
                              : `Current ${toSentenceCase(
                                  current.interval,
                                )} To Date`
                          }`}
                        </Typography.Body>
                      )}
                    </div>

                    <Inputs.Checkbox
                      testId={`${testPrefix}relative-to-date`}
                      isSmall
                      isChecked={current.currentToDate}
                      onToggled={(newValue) => {
                        setCurrent({
                          ...current,
                          currentToDate: newValue,
                        });
                      }}
                    />
                  </>
                )}
              </Row>
            </Row>
          </>
        )}
      </div>
    </Row>
  </div>
);

export default RelativeDateBuilder;
